import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  calculateMaxDateFromValidatorsFor,
  calculateMinDateFromValidatorsFor
} from '@aviary/forms/fields/date-form-field/date.form-validators';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerInputEvent,
  MatDatepickerToggle
} from '@angular/material/datepicker';
import {toLocalMidnight, toSimpleDateString, toSimpleDateTimeString} from '@aviary/store/common/date.helpers';
import {hasValue, isPopulatedString, isUndefined} from '@aviary/store/common/typing.helpers';
import {DateString, TimeOfDay} from '@aviary/store/common/common.types';
import {ControlRef} from '@aviary/forms/fields/form-fields.types';
import {MatInput, MatSuffix} from '@angular/material/input';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {MatIcon} from '@angular/material/icon';
import {ReactiveFormsModule} from '@angular/forms';
import {ExtendedFormControl} from '@aviary/forms/extensions/extended-form-control/extended-form-control';
import {getExtendedControlFrom} from '@aviary/forms/form.helpers';
import {BaseFormFieldComponent} from '@aviary/forms/fields/base-form-field/base-form-field.component';
import {provideControlRefTokenFor} from '@aviary/providers/control-ref.provider';
import {createDeferredInitializer, DeferredInitializerReturnValue} from '@aviary/store/common/initialization.helpers';

interface InitializationValues {
  fieldName: string;
  formGroup: ExtendedFormGroup;
  includeTime: TimeOfDay;
}

@Component({
  selector: 'mx-date-form-field',
  template: `
    <mx-base-form-field [fieldName]="fieldName" [overrideLabel]="overrideLabel" [externalLabel]="externalLabel"
                        [formGroup]="formGroup" [control]="control"
                        [hintLabel]="hintLabel">
      <div class="flex flex-row justify-between w-full">
        <!-- This <input> element is connected to the form control, but it's hidden in the UI.  It holds the actual form field value. -->
        <input matInput type="text" [formControl]="control" class="hidden"/>

        <input matInput type="text" #visibleDateInput [matDatepicker]="picker" class="min-w-0 w-24 grow" [min]="minDate"
               [max]="maxDate" [value]="dateValue"
               (dateChange)="onDateChange($event)"/>

        <mat-datepicker-toggle matSuffix [for]="picker" class="[&>button>svg]:hidden">
          <mat-icon matDatepickerToggleIcon class="material-icons-outlined">calendar_today</mat-icon>
        </mat-datepicker-toggle>
      </div>
    </mx-base-form-field>

    <mat-datepicker #picker panelClass="mx-date-form-field-picker"/>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideControlRefTokenFor(DateFormFieldComponent)],
  imports: [MatInput, MatDatepickerToggle, MatIcon, MatDatepicker, ReactiveFormsModule, MatDatepickerInput, MatSuffix, BaseFormFieldComponent]
})
export class DateFormFieldComponent implements OnInit, ControlRef, AfterViewInit {

  private readonly deferredInitializer: DeferredInitializerReturnValue<InitializationValues> = createDeferredInitializer({
    keysToInitialize: ['fieldName', 'formGroup', 'includeTime'],
    onAllKeysInitialized: this.initialize.bind(this),
    keysToAlwaysUpdate: ['fieldName', 'formGroup', 'includeTime']
  });

  @ViewChild('visibleDateInput', {read: MatInput}) visibleDateInput: MatInput;

  @Input({required: true})
  public set fieldName(fieldName: string) {
    this._fieldName = fieldName;

    this.deferredInitializer.setValue('fieldName', fieldName);
  }

  public get fieldName(): string {
    return this._fieldName;
  }

  @Input() public overrideLabel?: string;
  @Input() public externalLabel = false;
  @Input() public hintLabel: string = '';

  @Input({required: true})
  public set formGroup(formGroup: ExtendedFormGroup) {
    this._formGroup = formGroup;

    this.deferredInitializer.setValue('formGroup', formGroup);
  }

  public get formGroup(): ExtendedFormGroup {
    return this._formGroup;
  }


  @Input()
  public set includeTime(includeTime: TimeOfDay) {
    this._includeTime = includeTime;

    // If the parent calls this setter, this will trigger the deferred initializer once all other dependent inputs are set.  (If not, see ngOnInit().)
    this.deferredInitializer.setValue('includeTime', includeTime);
  }

  /*
    NOTE:  minDate/maxDate are purposely not exposed as inputs.  When using matDatepicker [min] and [max] inputs, or even [matDatepickerFilter], we can't really
    express what we mean (such as specifically "a future day", as opposed to "minimum = a day that happens to be tomorrow"), and we get hidden validators that
    don't give us the context we need to show good validation error messages (like "Must be in the future" vs. the not-as-specific "Must be 01/01/2024 or later"
    when tomorrow is 01/01/2024).

    So, we should add our own validators to our DateValidators namespace (which can then be visible in the form field's definition like normal).  And the
    presence of appropriate validators will then cause this class's ngOnInit() to set minDate/maxDate to properly disable dates in the picker. Then we can
    write messages appropriate to our validators in FieldErrorComponent.  (Angular Material's built-in validation messages will still also be present in the
    errors object, but as long as we capture our own validation errors first, we can safely ignore the built-in ones.)
  */
  public minDate?: Date;
  public maxDate?: Date;

  public dateValue?: Date | string;  // String only at first because we initialize it that way.  Once the user interacts, it's always a Date.
  public control: ExtendedFormControl;  // It's tempting to rename this to "formControl", but that's a reserved name and causes confusing errors.  So don't.

  private initialized: boolean = false;  // Prevents event emissions when initializing the form control.
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private latestPatchedValue?: DateString;
  private _fieldName: string;
  private _formGroup: ExtendedFormGroup;
  private _includeTime: TimeOfDay = 'NEITHER';

  public ngOnInit(): void {
    // If the optional includeTime setter wasn't called before we got here, then the deferred initializer hasn't had a chance to initialize yet...
    if (!this.deferredInitializer.isInitialized()) {
      // ...so set the value needed to trigger it now.
      this.deferredInitializer.setValue('includeTime', this._includeTime);
    }
  }

  public ngAfterViewInit(): void {
    this.control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (isPopulatedString(value)) {
          if (value !== this.latestPatchedValue) { // Don't chance infinite loops
            this.syncValuesFrom(value);
          }
        } else {
          this.visibleDateInput.value = ''; // Value is empty, possibly due to a form reset.  Set the visible date input field to match!
          this.latestPatchedValue = undefined;
        }
      });
  }

  public onDateChange(event: MatDatepickerInputEvent<Date>): void {
    this.syncValuesFrom(event.value);
  }

  private initialize(): void {
    this.control = getExtendedControlFrom(this._formGroup, this._fieldName);
    this.minDate = calculateMinDateFromValidatorsFor(this.control);
    this.maxDate = calculateMaxDateFromValidatorsFor(this.control);

    if (hasValue(this.control.value)) {
      this.syncValuesFrom(this.control.value);
    }

    this.initialized = true;
  }

  private syncValuesFrom(value: Date | string | null): void {
    this.latestPatchedValue = value ? this.getSimpleDateString(value) : undefined; // set latest patched value to simplified date string
    this.dateValue = value ? toLocalMidnight(value) : undefined;
    this.updateFormFieldValue();
  }

  private updateFormFieldValue(): void {
    this.control.patchValue(this.newFormFieldValue, {emitEvent: this.initialized});

    // Only mark as touched/dirty after the initial form data is applied
    if (this.initialized) {
      this.control.markAsTouched();  // We have to do this manually since the display <input> is not the same as the form-connected <input>.
      this.control.markAsDirty();
    }
  }

  private get newFormFieldValue(): DateString {
    if (isUndefined(this.dateValue)) return '';
    return this.getSimpleDateString(this.dateValue);
  }

  private getSimpleDateString(value: string | Date): DateString {
    return this._includeTime === 'NEITHER' ? toSimpleDateString(value) : toSimpleDateTimeString(value, this._includeTime);
  }
}
